<template>
  <div class="animated fadeIn">
    <b-nav 
      tabs 
      class="mb-3"
    >
      <b-nav-item 
        v-for="j in yearRange" 
        :key="j" 
        :to="{ name: 'Facturen', params: { jaar: j } }"
        :active="jaar == j"
      >
        Facturen {{ j }}
        <i 
          v-if="isBusy && jaar == j" 
          class="fa fa-hourglass"
        />
      </b-nav-item>
    </b-nav>

    <b-button-toolbar 
      class="mb-3"
    >
      <b-button 
        :to="{ name: 'FactuurNieuw' }" 
        icon="fa fa-save" 
        variant="success" 
        class="mr-2"
      >
        <i class="fa fa-plus" /> Nieuwe factuur aanmaken
      </b-button>
      <b-input-group 
        :append="'in ' + facturenoverzicht.length + ' facturen'" 
        prepend="Zoek"
      >
        <b-form-input 
          v-model="zoekterm" 
          placeholder="..." 
          type="search" 
          debounce="500"
        />
      </b-input-group>
      <b-button-group class="ml-2">
        <b-button :pressed.sync="alleenperiodiek">
          <i class="fa fa-refresh" /> Filter alleen periodieke facturen
        </b-button>

        <b-button 
          v-if="alleenperiodiek" 
          :disabled="facturenoverzicht.length == 0" 
          variant="warning" 
          size="sm"
          @click="selectAllRows()"
        >
          Alle {{ facturenoverzicht.length }} selecteren
        </b-button>

        <b-button 
          v-if="teherhalen.length > 0" 
          :pressed.sync="showHerhaalModal" 
          variant="success"
          @click="showHerhaalModal = true"
        >
          <i class="fa fa-magic" />
          Start wizard voor
          {{ teherhalen.length == 1 ? "factuur" : "facturen" }} ({{
            teherhalen.length
          }}
          geselecteerd)
        </b-button>
      </b-button-group>
      <b-button 
        :pressed.sync="alleenopenstaand" 
        class="ml-2" 
        variant="primary"
      >
        <i class="fa fa-filter" /> Openstaand
      </b-button>
      <b-button 
        v-if="!alleenperiodiek" 
        :pressed.sync="alleenconcept" 
        class="mx-2" 
        variant="warning"
      >
        <i class="fa fa-filter" /> Concept
      </b-button>
    </b-button-toolbar>
    <nav>
      <b-pagination
        v-model="options.pagination.currentPage"
        :total-rows="facturenoverzicht.length"
        :per-page="options.pagination.perPage"
        small
        responsive="sm"
        prev-text="Vorige"
        next-text="Volgende"
        limit="10"
        align="left"
      >
        <template #page="{ page, active, numberOfpages }">
          <span v-if="active">Pagina {{ page }} van {{ Math.ceil(facturenoverzicht.length/options.pagination.perPage) }}</span>
          <span v-else>{{ page }}</span>
          <span>{{ numberOfpages }}</span>
        </template>
      </b-pagination>
    </nav>
    <b-row>
      <b-col>
        <b-table 
          id="dataTable" 
          ref="overzichttabel" 
          :filter="zoekterm" 
          :fields="fields" 
          :items="facturenoverzicht"
          :filter-function="filterFunction" 
          :busy.sync="isBusy" 
          :empty-text="'Geen facturen voor ' + jaar" 
          :current-page="options.pagination.currentPage"
          :per-page="options.pagination.perPage"
          small
          bordered 
          show-empty 
          fixed 
          responsive 
          primary-key="Id" 
          empty-filtered-text="Geen resultaat" 
          selectable
          
          no-select-on-click 
          @row-selected="onRowSelected"
        >
          <template #empty>
            <h4>
              Geen
              {{ alleenperiodiek ? "periodieke facturen" : "facturen" }} voor
              {{ jaar }}
            </h4>
          </template>

          <template #table-colgroup="scope">
            <col 
              v-for="field in scope.fields" 
              :key="field.key" 
              :style="{ width: (field.width || 180) + 'px' }"
            >
          </template>
          <template #emptyfiltered="scope">
            <h4>{{ scope.emptyFilteredText }}</h4>
          </template>

          <template slot="table-busy">
            <div class="text-center text-info my-2">
              <b-spinner class="align-middle" />
              <strong class="mx-2">Laden...</strong>
            </div>
          </template>

          <template #cell(Factuurnummer)="props">
            <div v-if="props.item.Factuurnummer">
              {{ props.item.Factuurnummer }}
            </div>
            <div v-else>
              {{ props.item.Id }}
              <b-badge variant="warning">
                concept
              </b-badge>
            </div>
          </template>

          <template slot="empty">
            geen facturen voor {{ jaar }}
          </template>

          <template #cell(Datum)="props">
            {{ props.item.Datum | formatDateTime("DD-MM-YYYY") }}

            <b-button 
              v-if="
                props.item.IsHerhaling == null &&
                  props.item.Herhalen &&
                  props.item.HerhalingIs == null
              " 
              :pressed.sync="props.rowSelected" 
              class="btn-sm ml-1" 
              variant="warning" 
              @click="
                props.rowSelected ? props.selectRow() : props.unselectRow()
              "
            > 
              <i 
                v-if="props.item.Herhalen" 
                class="fa fa-refresh"
              /> Herhalen in
              {{ props.item.Prijspeiljaar + 1 }}
            </b-button>

            <b-button 
              v-if="props.item.HerhalingIs != null" 
              :to="{
                name: 'Factuur',
                params: { id: props.item.HerhalingIs.RemoteId }
              }" 
              variant="success" 
              class="btn-sm ml-1"
            >
              Factuur {{ props.item.HerhalingIs.Prijspeiljaar }}
            </b-button>
          </template>

          <template #cell(Herinnering)="props">
            {{ props.item.EersteHerinnering | formatDateTime("DD-MM-YYYY") }} | 
            {{ props.item.LaatsteHerinnering | formatDateTime("DD-MM-YYYY") }}
            
            <b-button 
              v-if="
                !props.item.IsBetaald &&
                  moment(props.item.Datum)
                    .add(30, 'days')
                    .isBefore(moment(new Date()))
              " 
              size="sm"
              variant="primary"
              @click="() => composeHerinnering(props.item)"
            >
              <font-awesome-icon icon="envelope" /> Herinnering
            </b-button>
          </template>

          <template #cell(Relatie)="props">
            <b-link 
              v-if="props.item.Relatie" 
              :to="{ name: 'Relatie', params: { id: props.item.Relatie.Id } }"
            >
              {{ props.item.Relatie.Naam
              }}
            </b-link>
            <div v-else>
              Geen relatie geselecteerd!
            </div>
          </template>

          <template #cell(VerzendenNaar)="props">
            {{ props.item.VerzendenNaar }}
            <i 
              v-if="props.item.AutomatischUitsturen" 
              class="fa fa-send"
            />
          </template>

          <template #cell(Totaal)="props">
            <span class="pull-right">{{
              totaal(props.item) | formatNumber()
            }}</span>
          </template>

          <template #cell(IsBetaald)="row">
            <b-spinner 
              v-if="row.item.busy" 
              small
            />
            <b-check 
              :disabled="row.item.busy"
              :checked="row.item.IsBetaald" 
              @input="() => onIsBetaaldChanged(row.item)"
            />
          </template>

          <template #cell(Bewerken)="props">
            <b-button 
              variant="warning" 
              size="sm" 
              class="mr-2" 
              @click="toonPreview(props.item.RemoteId)"
            >
              <font-awesome-icon icon="eye" /> Preview
            </b-button>
            <b-button 
              :to="{ name: 'Factuur', params: { id: props.item.RemoteId } }" 
              size="sm"
            >
              <i class="fa fa-edit" /> bewerken
            </b-button>
          </template>
        </b-table>
      </b-col>
      <b-modal 
        v-model="showPreview" 
        ok-only 
        ok-title="Sluiten" 
        size="xl" 
        title="Voorbeeld factuur"
      >
        <pdf :src="factuurPreview" />
      </b-modal>
    </b-row>

    <b-modal 
      v-model="showHerinneringModal" 
      title="Verstuur een herinnering" 
      size="lg" 
      ok-variant="secondary"
      ok-only
      ok-title="Annuleren"
      @hidden="factuur = null"
    >
      <RelatieHerinnering 
        :factuur="factuur" 
        @success="onHerinneringSuccess"
        @error="onHerinneringError"
      />
    </b-modal>

    <b-modal 
      v-model="showHerhaalModal" 
      :title="teherhalen.length + ' herhaalfacturen'" 
      size="xl"
      @hidden="$refs.overzichttabel.clearSelected()"
    >
      <factuur-modal :facturen="teherhalen" />
    </b-modal>
  </div>
</template>

<script>
import vSelect from "vue-select";
import { Switch as cSwitch } from "@coreui/vue";
import { setupCalendar, DatePicker } from "v-calendar";
import "v-calendar/lib/v-calendar.min.css";

import FactuurModal from "@/components/HerhaalFacturen";
import { facturatieMixin } from "@/mixins/facturatieMixin";
import { serviceMixin } from "@/mixins/serviceMixin";
import pdf from "vue-pdf";
import RelatieHerinnering from "../../settings/RelatieHerinnering.vue";
setupCalendar({
  firstDayOfWeek: 2 // Monday
});

export default {
  name: "Facturen",
  components: {
    pdf,
    cSwitch,
    vSelect,
    "v-date-picker": DatePicker,
    "factuur-modal": FactuurModal,
    RelatieHerinnering
  },
  mixins: [facturatieMixin, serviceMixin],
  props: {
    jaar: {
      type: Number,
      default: new Date().getFullYear()
    },
    relatie: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      showPreview: false,
      factuurPreview: null,
      isBusy: false,
      showHerhaalModal: false,
      showHerinneringModal: false,
      concepten: [],
      facturen: [],
      teherhalen: [],
      alleenperiodiek: false,
      alleenconcept: false,
      alleenopenstaand: false,
      factuur: null,
      fields: [
        { key: "Factuurnummer", label: "#", width: 50 },
        { key: "Datum", width: 200 },
        { key: "Herinnering" },
        { key: "Relatie" },
        "Onderwerp",
        { key: "VerzendenNaar", label: "E-mail" },
        { key: "Totaal", width: 150 },
        { key: "IsBetaald", label: "Betaald", width: 70 },
        { key: "Bewerken", width: 250 }
      ],
      zoekterm: null,
      options: {
        pagination: {
          currentPage: 1,
          perPage: 30,
          perPageOpties: [15,30,60,90]
        }
      }
    };
  },
  computed: {
    yearRange() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let year = currentYear; year >= 2019; year--) {
        years.push(year);
      }
      return years;
    },
    facturenoverzicht: function () {
      if (this.alleenconcept) return this.concepten;
      if (this.alleenopenstaand) return this.openstaand;
      var lijst = [];
      this.facturen.forEach(f => lijst.push(f));
      if (this.jaar == new Date().getFullYear())
        this.concepten.forEach(f => lijst.push(f));
      if (this.relatie == null)
        return lijst.filter(f => !this.alleenperiodiek || f.Herhalen);
      return lijst.filter(
        f =>
          (!this.alleenperiodiek || f.Herhalen) &&
          (f.Relatie ? f.Relatie.Id : 0) == this.relatie.Id
      );
    },
    openstaand: function () {
      return this.facturen.filter(f => !f.IsBetaald);
    },
  },
  watch: {
    jaar: function (oldval, newval) {
      console.log(newval);
      this.getFacturen();
    }
  },
  mounted() {
    this.getFacturen();
    this.getConcepten();
  },
  methods: {
    onIsBetaaldChanged(item) {
      console.log(item);
      var vm = this;
      this.$set(item,"busy",true);
      this.post("/facturen/factuur/" + item.RemoteId + "/betaling", {
        IsBetaald: !item.IsBetaald
      })
        .then(response => {
          vm.facturen.find(f => f.RemoteId == item.RemoteId).IsBetaald = !item
            .IsBetaald;
          console.log(response);
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          this.$set(item,"busy",false);
        });
    },
    onHerinneringError(error) {
      console.log(error);
      this.showHerinneringModal = false;
      this.$bvToast.toast('Fout bij versturen!', {
        title: "Herinnering",
        variant: "danger",
        solid: true
      });
    },
    onHerinneringSuccess(result) {
      console.log(result)
      this.showHerinneringModal = false;
      this.$bvToast.toast('Herinnering verstuurd', {
        title: "Herinnering",
        variant: "success",
        solid: true
      });
    },
    selectAllRows() {
      this.$refs.overzichttabel.selectAllRows();
    },
    onRowSelected(items) {
      console.log(items.length);
      this.teherhalen = items;
    },
    gotoJaar(v) {
      console.log(v);
      this.$router.push({ name: "Facturen", params: { jaar: v } });
    },
    toonPreview(id) {
      //this.factuurPreview = null
      var vm = this;
      this.$http
        .get(`/facturen/factuur/${id}/preview`)
        .then(response => {
          //console.log(response.data.pdf)
          vm.factuurPreview = response.data.pdf;
          vm.showPreview = true;
        });
    },
    verstuurFactuur: function (id) {
      console.log("Verstuur factuur...");
      this.$http
        .get("/facturen/factuur/" + id + "/verzenden")
        .then(function (response) {
          console.log(response.data);
        })
        .catch(function (error) {
          console.log(error);
        });
    },
    filterFunction(item, filter) {
      var zoek = new RegExp(filter.toLowerCase());
      return zoek.test(
        (item.Relatie ? item.Relatie.Naam.toLowerCase() : "geen") +
        item.Onderwerp.toLowerCase() +
        item.Factuurnummer +
        this.totaal(item)
      );
    },
    // isGoingToBeSent(row) {
    //   return (
    //     row.AutomatischUitsturen &&
    //     moment(row.Datum).isSameOrBefore(
    //       moment(new Date()).add(1, "days"),
    //       "day"
    //     )
    //   );
    // },
    getConcepten: function () {
      var vm = this;
      this.$http
        .get("/facturen/proforma")
        .then(function (response) {
          //console.log(response);
          console.log(response.data);
          vm.concepten = response.data.facturen;
        })
        .catch(function (error) {
          console.log(error);
        })
        .then(function () {
          // always executed
        });
    },
    composeHerinnering(row) {
      console.log(row);
      this.factuur = row;
      this.showHerinneringModal = true;
    },
    getFacturen() {
      var vm = this;
      console.log("getFacturen");
      this.isBusy = true;
      this.$http
        .get("/facturen/" + this.jaar)
        .then(function (response) {
          vm.facturen = response.data.facturen;
        })
        .catch(function (error) {
          console.log(error);
        })
        .then(function () {
          // always executed
          vm.isBusy = false;
        });
    }
  }
};
</script>
